import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiFileText, FiEdit, FiTrash2, FiSearch, FiPlus, FiFilter, FiCalendar, FiCopy, FiUpload, FiLoader, FiActivity } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { resumeApi, diagnosisApi } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import ResumeNameEditor from '../../components/resumes/ResumeNameEditor';
import PageHeader from '../../components/layout/PageHeader';
import { pageDescriptions } from '../../data/pageDescriptions';
import { useCache } from '../../contexts/CacheContext';
import { useMembership } from '../../contexts/MembershipContext';

// ── extractTextFromContent (same as ResumeDiagnosis.js) ──────────────────────────

function extractTextFromContent(content) {
  if (!content) return '';
  const parts = [];
  if (content.name) parts.push(`Name: ${content.name}`);
  if (content.summary) parts.push(`Summary: ${content.summary}`);
  if (Array.isArray(content.experience)) {
    parts.push('Experience:');
    content.experience.forEach(exp => {
      if (!exp) return;
      parts.push(`  ${exp.title || ''} at ${exp.company || ''}`);
      const details = exp.details || exp.bullets || [];
      (Array.isArray(details) ? details : []).forEach(d => {
        const text = typeof d === 'string' ? d : (d?.details || '');
        if (text) parts.push(`    - ${text}`);
      });
    });
  }
  if (Array.isArray(content.education)) {
    parts.push('Education:');
    content.education.forEach(edu => {
      if (!edu) return;
      parts.push(`  ${edu.degree || ''} at ${edu.school || edu.institution || ''}`);
    });
  }
  if (content.skills) {
    const skills = Array.isArray(content.skills) ? content.skills : [];
    const names = skills.map(s => (typeof s === 'string' ? s : s?.name || '')).filter(Boolean);
    if (names.length) parts.push(`Skills: ${names.join(', ')}`);
  }
  return parts.join('\n');
}

function getErrorMessage(err, fallback = 'Diagnosis failed') {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  if (err?.response?.status === 404) return d || 'Resource not found';
  return fallback;
}

const ResumeList = () => {
  const navigate = useNavigate();
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedResumes, setSelectedResumes] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [resumeToDelete, setResumeToDelete] = useState(null);
  const [deleteBlockedByActiveApps, setDeleteBlockedByActiveApps] = useState(false);
  const [activeApplicationCount, setActiveApplicationCount] = useState(0);
  const [cloningResumeId, setCloningResumeId] = useState(null);
  const [quickUploading, setQuickUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [expandedDiagnosisResumeId, setExpandedDiagnosisResumeId] = useState(null);
  const [diagnosisData, setDiagnosisData] = useState({});
  const [diagnosingResumeId, setDiagnosingResumeId] = useState(null);
  const quickFileRef = useRef(null);
  
  const { getResumes, invalidateResumeCache } = useCache();
  const { refreshCreditBalance } = useMembership();

  const handleQuickUpload = useCallback(async (file) => {
    const validMimeTypes = ['application/pdf', 'application/x-pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    const ext = (file.name || '').split('.').pop()?.toLowerCase();
    const validExts = ['pdf', 'docx', 'doc'];
    if (!validMimeTypes.includes(file.type) && !validExts.includes(ext)) {
      toast.error('Please upload a PDF, DOCX, or DOC file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File must be less than 5MB');
      return;
    }

    try {
      setQuickUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('language', 'en');
      const response = await resumeApi.uploadResume(formData);
      await refreshCreditBalance();
      invalidateResumeCache();
      toast.success('Resume uploaded!');
      navigate(`/resumes/${response.data.id}`);
    } catch (err) {
      console.error('Quick upload failed:', err);
      if (err.response?.status === 402) {
        toast.error('Not enough credits to upload.');
      } else {
        toast.error(err.response?.data?.detail || 'Upload failed.');
      }
    } finally {
      setQuickUploading(false);
      setDragActive(false);
    }
  }, [refreshCreditBalance, invalidateResumeCache, navigate]);

  useEffect(() => {
    fetchResumes();
  }, []);
  
  const fetchResumes = async () => {
    try {
      setLoading(true);
      const resumes = await getResumes();
      setResumes(resumes);
    } catch (error) {
      console.error('Error fetching resumes:', error);
      toast.error('Failed to load resumes');
    } finally {
      setLoading(false);
    }
  };

  
  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortBy(field);
      setSortDirection('desc');
    }
  };
  
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };
  
  const toggleResumeSelection = (resumeId) => {
    if (selectedResumes.includes(resumeId)) {
      setSelectedResumes(selectedResumes.filter(id => id !== resumeId));
    } else {
      setSelectedResumes([...selectedResumes, resumeId]);
    }
  };
  
  const confirmDelete = (resume) => {
    setResumeToDelete(resume);
    setDeleteBlockedByActiveApps(false);
    setActiveApplicationCount(0);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteBlockedByActiveApps(false);
    setActiveApplicationCount(0);
  };
  
  const handleDelete = async (force = false) => {
    try {
      await resumeApi.deleteResume(resumeToDelete.id, force ? { params: { force: true } } : {});
      
      // Update UI optimistically
      setResumes(resumes.filter(resume => resume.id !== resumeToDelete.id));
      setSelectedResumes(selectedResumes.filter(id => id !== resumeToDelete.id));
      
      // Invalidate the cache
      invalidateResumeCache();
      
      toast.success('Resume deleted successfully');
      closeDeleteModal();
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.detail?.activeApplicationCount != null) {
        setDeleteBlockedByActiveApps(true);
        setActiveApplicationCount(error.response.data.detail.activeApplicationCount);
      } else {
        console.error('Error deleting resume:', error);
        toast.error('Failed to delete resume');
      }
    }
  };
  
  
  const handleCloneResume = async (resume) => {
    try {
      setCloningResumeId(resume.id);
      const response = await resumeApi.cloneResume(resume.id);
      
      if (response.data.success) {
        toast.success(`Resume "${resume.name}" cloned successfully!`);
        
        // Refresh the resume list
        await fetchResumes();
        
        // Invalidate cache
        invalidateResumeCache();
        
        // Navigate to the editing page of the cloned resume
        const newResumeId = response.data.newResumeId;
        navigate(`/resumes/${newResumeId}`);
      } else {
        toast.error('Failed to clone resume');
      }
    } catch (error) {
      console.error('Error cloning resume:', error);
      toast.error('Failed to clone resume');
    } finally {
      setCloningResumeId(null);
    }
  };

  const handleDiagnosisClick = async (resume) => {
    if (!resume?.id) return;
    const existing = diagnosisData[resume.id];
    if (existing?.error) {
      setDiagnosisData(prev => {
        const next = { ...prev };
        delete next[resume.id];
        return next;
      });
    }
    setDiagnosingResumeId(resume.id);
    setExpandedDiagnosisResumeId(resume.id);
    let resume_text = '';
    try {
      const versionRes = await resumeApi.getFirstResumeVersion(resume.id);
      const content = versionRes?.data?.content;
      resume_text = extractTextFromContent(content);
      const wordCount = (resume_text || '').trim().split(/\s+/).filter(Boolean).length;
      if (wordCount < 80) {
        toast.error('Resume is too short for diagnosis (min 80 words)');
        setExpandedDiagnosisResumeId(null);
        setDiagnosingResumeId(null);
        return;
      }
    } catch (err) {
      console.error('Could not load resume:', err);
      const msg = getErrorMessage(err, 'Could not load resume');
      setDiagnosisData(prev => ({ ...prev, [resume.id]: { error: true, message: `Could not load resume. ${msg}` } }));
      toast.error(`Could not load resume: ${msg}`);
      setDiagnosingResumeId(null);
      return;
    }
    try {
      // First check if there's a cached diagnosis for this resume (fast path)
      try {
        const cachedRes = await diagnosisApi.getForResume(resume.id);
        // Backend returns JSON body (diagnosis object) or null when none; 404 only if resume invalid
        const payload = cachedRes?.data;
        if (payload != null && typeof payload === 'object') {
          navigate('/resume-diagnosis/report', { state: { diagnosis: payload, resumeId: resume.id } });
          setDiagnosingResumeId(null);
          setExpandedDiagnosisResumeId(null);
          return;
        }
      } catch (cacheErr) {
        const status = cacheErr?.response?.status;
        // 404/401: fall through to runQuickDiagnosis; other errors are logged but still fall through
        if (status !== 404 && status !== 401) {
          console.warn('Diagnosis cache lookup failed:', status, cacheErr);
        }
      }

      // No cached diagnosis - resume_text already loaded above, generate new diagnosis
      const res = await diagnosisApi.runQuickDiagnosis({
        resume_text,
        target_roles: [],
        seniority: 'Not specified',
        location: 'Not specified',
        channel_preference: 'online',
        visa: null,
        resume_id: resume.id,
      });
      navigate('/resume-diagnosis/report', { state: { diagnosis: res.data, resumeId: resume.id } });
    } catch (err) {
      if (err.response?.status === 409) {
        const proceed = window.confirm(
          'Your resume has changed since the last diagnosis. Regenerate for 2 credits?'
        );
        if (!proceed) {
          setDiagnosisData(prev => ({
            ...prev,
            [resume.id]: {
              error: true,
              message: 'Diagnosis is outdated. Run again when you are ready (2 credits).',
            },
          }));
          setDiagnosingResumeId(null);
          return;
        }
        try {
          const res = await diagnosisApi.runQuickDiagnosis({
            resume_text,
            target_roles: [],
            seniority: 'Not specified',
            location: 'Not specified',
            channel_preference: 'online',
            visa: null,
            resume_id: resume.id,
            confirmRegenerate: true,
          });
          await refreshCreditBalance();
          navigate('/resume-diagnosis/report', { state: { diagnosis: res.data, resumeId: resume.id } });
        } catch (err2) {
          console.error('Diagnosis regenerate failed:', err2);
          const msg = getErrorMessage(err2, 'Diagnosis failed');
          setDiagnosisData(prev => ({
            ...prev,
            [resume.id]: { error: true, message: `Diagnosis service failed. ${msg}` },
          }));
          toast.error(`Diagnosis failed: ${msg}`);
        }
        setDiagnosingResumeId(null);
        return;
      }
      console.error('Diagnosis service failed:', err);
      const msg = getErrorMessage(err, 'Diagnosis failed');
      setDiagnosisData(prev => ({ ...prev, [resume.id]: { error: true, message: `Diagnosis service failed. ${msg}` } }));
      toast.error(`Diagnosis failed: ${msg}`);
    } finally {
      setDiagnosingResumeId(prev => (prev === resume.id ? null : prev));
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Filter and sort resumes
  const filteredAndSortedResumes = [...resumes]
    .filter(resume => 
      (resume.name || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let comparison = 0;
      if (sortBy === 'name') {
        comparison = (a.name || '').localeCompare(b.name || '');
      } else if (sortBy === 'createdAt') {
        comparison = new Date(a.createdAt) - new Date(b.createdAt);
      } else if (sortBy === 'updatedAt') {
        comparison = new Date(a.lastUpdatedAt) - new Date(b.lastUpdatedAt);
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  
  if (loading) {
    return <LoadingSpinner message="Loading resumes..." />;
  }
  
  const copy = pageDescriptions['/resumes'];

  return (
    <div className="space-y-6">
      <PageHeader
        title={copy.title}
        description={copy.description}
        whatYouCanDo={copy.whatYouCanDo}
        whatYouShouldDo={copy.whatYouShouldDo}
        actions={
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => quickFileRef.current?.click()}
              disabled={quickUploading}
              aria-label="Upload resume"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {quickUploading ? (
                <><FiLoader className="animate-spin -ml-1 mr-2 h-4 w-4" /> Uploading...</>
              ) : (
                <><FiUpload className="-ml-1 mr-2 h-4 w-4" /> Upload Resume</>
              )}
            </button>
            <input
              type="file"
              className="hidden"
              ref={quickFileRef}
              accept=".pdf,.docx,.doc"
              onChange={(e) => {
                if (e.target.files?.[0]) handleQuickUpload(e.target.files[0]);
                e.target.value = '';
              }}
            />
          </div>
        }
      />

      {/* Inline drop zone */}
      <div
        className={`border-2 border-dashed rounded-xl p-4 text-center transition-all duration-200 ${
          dragActive
            ? 'border-primary-400 bg-primary-50 scale-[1.01]'
            : 'border-gray-200 hover:border-gray-300 bg-gray-50/50'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
        onDrop={(e) => {
          e.preventDefault();
          setDragActive(false);
          if (e.dataTransfer.files?.[0]) handleQuickUpload(e.dataTransfer.files[0]);
        }}
        onClick={() => !quickUploading && quickFileRef.current?.click()}
      >
        {quickUploading ? (
          <div className="flex items-center justify-center gap-2 py-1 text-sm text-primary-600">
            <FiLoader className="animate-spin h-4 w-4" />
            <span>Uploading and parsing...</span>
          </div>
        ) : (
          <p className={`text-sm ${dragActive ? 'text-primary-600 font-medium' : 'text-gray-400'}`}>
            {dragActive ? 'Drop to upload!' : 'Drop a resume here to quick-upload (PDF, DOCX, DOC)'}
          </p>
        )}
      </div>

      {/* Search and Filters */}
      <div className="card p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="h-12 text-base focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 border-gray-300 rounded-md"
              placeholder="Search resumes..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="flex flex-row gap-4">
            <div className="w-full md:w-auto">
              <label htmlFor="sort-by" className="sr-only">Sort by</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiFilter className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  id="sort-by"
                  name="sort-by"
                  className="h-12 text-base focus:ring-primary-500 focus:border-primary-500 block w-full pl-10 border-gray-300 rounded-md"
                  value={sortBy}
                  onChange={(e) => handleSort(e.target.value)}
                >
                  <option value="updatedAt">Last Updated</option>
                  <option value="createdAt">Date Created</option>
                  <option value="name">File Name</option>
                </select>
              </div>
            </div>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
            >
              {sortDirection === 'asc' ? 'Ascending' : 'Descending'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Resumes List */}
      <div className="card overflow-hidden">
        {filteredAndSortedResumes.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {filteredAndSortedResumes.map((resume) => (
              <React.Fragment key={resume.id}>
                <li>
                  <div className="px-4 py-4 flex items-center sm:px-6">
                    <div className="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-12 w-12 rounded-md bg-primary-100 flex items-center justify-center">
                            <FiFileText className="h-6 w-6 text-primary-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-primary-600 truncate">
                            <ResumeNameEditor 
                              resumeId={resume.id} 
                              initialName={resume.name} 
                              className="text-sm"
                              onUpdateSuccess={(updatedName) => {
                                // Update the resume name in the local state
                                setResumes(resumes.map(r => 
                                  r.id === resume.id ? {...r, name: updatedName} : r
                                ));
                              }}
                            />
                          </div>
                          <div className="flex mt-1">
                            <div className="flex items-center text-sm text-gray-500">
                              <FiCalendar className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                              <span>Updated {formatDate(resume.lastUpdatedAt)}</span>
                            </div>
                            <div className="ml-4 flex items-center text-sm text-gray-500">
                              <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                {(resume.language || 'en').toUpperCase()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-4 flex-shrink-0 sm:mt-0 sm:ml-5">
                        <div className="flex space-x-4">

                        <button
                            type="button"
                            onClick={() => handleCloneResume(resume)}
                            disabled={cloningResumeId === resume.id}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                            <FiCopy className="-ml-0.5 mr-1 h-4 w-4" />
                            {cloningResumeId === resume.id ? 'Cloning...' : 'Clone'}
                          </button>
                          <Link
                            to={`/resumes/${resume.id}`}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-primary-700 bg-primary-100 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                          >
                            <FiEdit className="-ml-0.5 mr-1 h-4 w-4" />
                            Edit
                          </Link>

                          <button
                            type="button"
                            onClick={() => handleDiagnosisClick(resume)}
                            disabled={diagnosingResumeId === resume.id}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-teal-700 bg-teal-100 hover:bg-teal-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FiActivity className="-ml-0.5 mr-1 h-4 w-4" />
                            {diagnosingResumeId === resume.id ? 'Analyzing...' : 'Diagnosis'}
                          </button>

                          <button
                            type="button"
                            onClick={() => confirmDelete(resume)}
                            className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          >
                            <FiTrash2 className="-ml-0.5 mr-1 h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
                {expandedDiagnosisResumeId === resume.id && (
                  <li className="bg-gray-50 border-t-0">
                    <div className="px-4 py-4 sm:px-6 rounded-b-lg">
                      {diagnosingResumeId === resume.id ? (
                        <div className="flex items-center gap-3 py-6">
                          <FiLoader className="animate-spin h-5 w-5 text-primary-600" />
                          <span className="text-sm text-gray-600">Analyzing your resume...</span>
                        </div>
                      ) : diagnosisData[resume.id]?.error ? (
                        <div className="py-4">
                          <p className="text-sm text-red-600 mb-2">{diagnosisData[resume.id]?.message || 'Diagnosis failed. Please try again.'}</p>
                          <button
                            type="button"
                            onClick={() => handleDiagnosisClick(resume)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded text-teal-700 bg-teal-100 hover:bg-teal-200"
                          >
                            Retry
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </li>
                )}
              </React.Fragment>
            ))}
          </ul>
        ) : (
          <div className="py-12 text-center">
            <FiFileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No resumes found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try a different search term' : 'Get started by uploading a resume'}
            </p>
            {!searchTerm && (
              <div className="mt-6">
                <Link
                  to="/resumes/upload"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <FiPlus className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                  Upload Resume
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && resumeToDelete && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-xl border border-gray-200 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <FiTrash2 className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">Delete Resume</h3>
                    <div className="mt-2">
                      {deleteBlockedByActiveApps ? (
                        <>
                          <p className="text-sm text-gray-500">
                            This resume is linked to {activeApplicationCount} active application(s) (Applied or Interviewing). If you delete it, you will not be able to view the version you used for those applications.
                          </p>
                          <p className="text-sm font-medium text-gray-900 mt-2">
                            {resumeToDelete.name}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm text-gray-500">
                            Are you sure you want to delete this resume? This action cannot be undone.
                          </p>
                          <p className="text-sm font-medium text-gray-900 mt-2">
                            {resumeToDelete.name}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => handleDelete(deleteBlockedByActiveApps)}
                >
                  {deleteBlockedByActiveApps ? 'Delete anyway' : 'Delete'}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={closeDeleteModal}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumeList;